import { LoginPage } from "@/auth/pages/LoginPage";
import { LogoutPage } from "@/auth/pages/LogoutPage";
import { lazy } from "react";
import { Navigate, Route, Routes as ReactRouterRoutes } from "react-router";
import { ProtectedRoute } from "./ProtectedRoute";
import { SettingsLayout } from "@/settings/components/SettingsLayout";

const DashboardPage = lazy(() =>
    import("@/dashboard/pages/DashboardPage").then((module) => ({
        default: module.DashboardPage,
    })),
);

const GmailOAuthPage = lazy(() =>
    import("@/settings/pages/GmailOAuthPage").then((module) => ({
        default: module.GmailOAuthPage,
    })),
);

const ToneProfilesPage = lazy(() =>
    import("@/settings/pages/ToneProfilesPage").then((module) => ({
        default: module.ToneProfilesPage,
    })),
);

const CategoriesPage = lazy(() =>
    import("@/settings/pages/CategoriesPage").then((module) => ({
        default: module.CategoriesPage,
    })),
);

const AddonPage = lazy(() =>
    import("@/settings/pages/AddonPage").then((module) => ({
        default: module.AddonPage,
    })),
);

const CompanyPage = lazy(() =>
    import("@/settings/pages/CompanyPage").then((module) => ({
        default: module.CompanyPage,
    })),
);

const TeamPage = lazy(() =>
    import("@/settings/pages/TeamPage").then((module) => ({
        default: module.TeamPage,
    })),
);

const PrivacyPage = lazy(() =>
    import("@/settings/pages/PrivacyPage").then((module) => ({
        default: module.PrivacyPage,
    })),
);

// Onboarding wizard — outside ProtectedRoute to avoid infinite redirect loops (spec 0013)
const OnboardingLayout = lazy(() =>
    import("@/onboarding/components/OnboardingLayout").then((module) => ({
        default: module.OnboardingLayout,
    })),
);
const CompanyDetailsStep = lazy(() =>
    import("@/onboarding/pages/CompanyDetailsStep").then((module) => ({
        default: module.CompanyDetailsStep,
    })),
);
const ConnectGmailStep = lazy(() =>
    import("@/onboarding/pages/ConnectGmailStep").then((module) => ({
        default: module.ConnectGmailStep,
    })),
);
const InvitesStep = lazy(() =>
    import("@/onboarding/pages/InvitesStep").then((module) => ({
        default: module.InvitesStep,
    })),
);
const DoneStep = lazy(() =>
    import("@/onboarding/pages/DoneStep").then((module) => ({
        default: module.DoneStep,
    })),
);

// Public invite-accept flow — no auth gate (spec 0013C)
const InviteAcceptPage = lazy(() =>
    import("@/auth/pages/InviteAcceptPage").then((module) => ({
        default: module.InviteAcceptPage,
    })),
);

export function Routes() {
    return (
        <ReactRouterRoutes>
            <Route path="auth">
                <Route path="sign-in" element={<LoginPage />} />
                <Route path="logout" element={<LogoutPage />} />
            </Route>

            {/* Onboarding wizard — outside ProtectedRoute (spec 0013 AC-1) */}
            <Route path="onboarding" element={<OnboardingLayout />}>
                <Route path="company-details" element={<CompanyDetailsStep />} />
                <Route path="connect-gmail" element={<ConnectGmailStep />} />
                <Route path="invites" element={<InvitesStep />} />
                <Route path="done" element={<DoneStep />} />
            </Route>

            {/* Public invite-accept flow (spec 0013 AC-11) */}
            <Route path="invite/:token" element={<InviteAcceptPage />} />

            <Route element={<ProtectedRoute />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="settings" element={<SettingsLayout />}>
                    <Route path="gmail-oauth" element={<GmailOAuthPage />} />
                    <Route path="tone-profiles" element={<ToneProfilesPage />} />
                    <Route path="categories" element={<CategoriesPage />} />
                    <Route path="addon" element={<AddonPage />} />
                    <Route path="company" element={<CompanyPage />} />
                    <Route path="team" element={<TeamPage />} />
                    <Route path="privacy" element={<PrivacyPage />} />
                </Route>
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </ReactRouterRoutes>
    );
}
